import { AxiosResponse } from 'axios';

import { ERoutes } from '../../assets/constants/routes';
import { EEndpoint } from '../../config/endpoint.config';
import { IEntity } from '../../domain/entity.interface';
import { TGroupEmail, TGroupStateKeys } from '../../domain/group.interface';
import {
  ENotification,
  INotification,
} from '../../domain/notification.interface';
import { HttpService } from '../../services/services.index';
import history from '../../store/history/history';
import { returnNotification } from '../render-controller/render-controller.actions';

export const GROUPLIST_LOADED = 'GROUPLIST_LOADED';
export const GROUP_COMPLETE_LOADED = 'GROUP_COMPLETE_LOADED';
export const GROUP_INFO_LOADED = 'GROUP_INFO_LOADED';
export const GROUP_MEMBERS_LOADED = 'GROUP_MEMBERS_LOADED';
export const GROUP_WISHES_LOADED = 'GROUP_WISHES_LOADED';
export const GROUP_MONEYBOX_LOADED = 'GROUP_MONEYBOX_LOADED';
export const GROUP_RESET = 'GROUP_RESET';

const httpService = new HttpService();

export const loadGroupList = () => {
  return (dispatch: any) => {
    httpService
      .get(EEndpoint.GROUP_LIST)
      .then((res: AxiosResponse<IEntity[]>) => {
        dispatch({
          type: GROUPLIST_LOADED,
          payload: res,
        });
      });
  };
};

export const resetGroup = (key: TGroupStateKeys) => {
  return (dispatch: any) => {
    dispatch({
      type: GROUP_RESET,
      payload: key,
    });
  };
};

export const loadGroupComplete = (groupId: string) => {
  return (dispatch: any) => {
    httpService
      .get(EEndpoint.GROUP_ID, { params: { groupId: groupId } })
      .then((res: AxiosResponse<IEntity>) => {
        dispatch({
          type: GROUP_COMPLETE_LOADED,
          payload: res,
        });
      });
  };
};

export const loadGroupInfo = (groupId: string) => {
  return (dispatch: any) => {
    httpService
      .get(EEndpoint.GROUP_RESUME, { params: { groupId: groupId } })
      .then((res: AxiosResponse<IEntity>) => {
        dispatch({
          type: GROUP_INFO_LOADED,
          payload: res,
        });
      });
  };
};

export const loadGroupMembers = (groupId: string) => {
  return (dispatch: any) => {
    httpService
      .get(EEndpoint.GROUP_MEMBERS, { params: { groupId: groupId } })
      .then((res: AxiosResponse<IEntity>) => {
        dispatch({
          type: GROUP_MEMBERS_LOADED,
          payload: res,
        });
      });
  };
};

export const loadGroupWishes = (groupId: string) => {
  return (dispatch: any) => {
    httpService
      .get(EEndpoint.GROUP_WISHES, { params: { groupId: groupId } })
      .then((res: AxiosResponse<IEntity>) => {
        dispatch({
          type: GROUP_WISHES_LOADED,
          payload: res,
        });
      });
  };
};

export const addGroupWishes = (ids: { id: string }[], groupId: string) => {
  return httpService.patch(EEndpoint.GROUP_ADD_WISHES, ids, {
    params: { groupId: groupId },
  });
};

export const newGroupWish = (body: any, images: any, groupId?: string) => {
  return async (dispatch: any) => {
    const newBody = body;
    newBody.groupId = groupId;
    const res: any = await httpService.post(EEndpoint.WISH, newBody);

    if (!res) {
      return;
    }

    if (images.hasOwnProperty('main_photo')) {
      await httpService.post(
        EEndpoint.WISH_MAIN_PHOTO_UPLOAD,
        images['main_photo'],
        {
          params: { wishId: res.id },
        },
      );
    }

    if (images.hasOwnProperty('optional_photos')) {
      await httpService.post(
        EEndpoint.WISH_OPTIONAL_PHOTO_UPLOAD,
        images['optional_photos'],
        { params: { wishId: res.id } },
      );
    }

    if (groupId) {
      dispatch(loadGroupWishes(groupId));
    }
  };
};

export const editGroupWish = (
  body: any,
  images: any,
  imagesToRemove: string[] | undefined,
  wishId: string,
  groupId?: string,
) => {
  return async (dispatch: any) => {
    const res: any = await httpService.put(EEndpoint.WISH_ID, body, {
      params: { wishId: wishId },
    });

    if (images.hasOwnProperty('main_photo')) {
      await httpService.post(
        EEndpoint.WISH_MAIN_PHOTO_UPLOAD,
        images['main_photo'],
        {
          params: { wishId: res.id },
        },
      );

      if (images.hasOwnProperty('optional_photos')) {
        await httpService.post(
          EEndpoint.WISH_OPTIONAL_PHOTO_UPLOAD,
          images['optional_photos'],
          { params: { wishId: res.id } },
        );
      }

      if (!!imagesToRemove && !!imagesToRemove.length) {
        await httpService.post(
          EEndpoint.WISH_OPTIONAL_PHOTO_DELETE_MANY,
          { ids: imagesToRemove },
          { params: { wishId: wishId } },
        );
      }
    }

    if (groupId) {
      dispatch(loadGroupWishes(groupId));
    }
  };
};

export const deleteGroupWish = (wishId: string, groupId?: string) => {
  return async (dispatch: any) => {
    await httpService.delete(EEndpoint.WISH_ID, { params: { wishId: wishId } });
    if (groupId) {
      dispatch(loadGroupWishes(groupId));
    }
  };
};

export const loadGroupMoneybox = (groupId: string) => {
  return (dispatch: any) => {
    httpService
      .get(EEndpoint.GROUP_MONEYBOX, { params: { groupId: groupId } })
      .then((res: AxiosResponse<IEntity>) => {
        dispatch({
          type: GROUP_MONEYBOX_LOADED,
          payload: res,
        });
      });
  };
};

export const likeGroupWish = (groupId: string, wishId: string) => {
  return (dispatch: any) => {
    httpService
      .patch(
        EEndpoint.GROUP_LIKE_WISH,
        {},
        {
          params: { groupId: groupId, wishId: wishId },
        },
      )
      .then((res: AxiosResponse<IEntity>) => {
        dispatch(loadGroupWishes(groupId));
      });
  };
};

export const likeGroupAmount = (groupId: string, amountId: string) => {
  return (dispatch: any) => {
    httpService
      .patch(
        EEndpoint.GROUP_LIKE_AMOUNT,
        {},
        {
          params: { amountId: amountId },
        },
      )
      .then((res: AxiosResponse<IEntity>) => {
        dispatch(loadGroupMoneybox(groupId));
      });
  };
};

export const setAsPaidGroupMember = (
  groupId: string,
  pendingPaymentId: string,
) => {
  return async (dispatch: any) => {
    await httpService.patch(
      EEndpoint.SET_AS_PAID,
      {},
      {
        params: { pendingPaymentId: pendingPaymentId },
      },
    );
    dispatch(loadGroupInfo(groupId));
    dispatch(loadGroupMembers(groupId));
    dispatch(loadGroupMoneybox(groupId));
  };
};

export const newGroup = (body: any, images: any) => {
  return async (dispatch: any) => {
    const res: any = await httpService.post(EEndpoint.GROUP_LIST, body);
    const imgObjKeys = Object.keys(images);
    for await (const key of imgObjKeys) {
      if (!!res && res.id) {
        await httpService.post(EEndpoint.GROUP_MAIN_PHOTO_UPLOAD, images[key], {
          params: {
            groupId: res.id,
          },
        });
      }
    }
    history.push(ERoutes.GROUP_LIST);
  };
};

export const submitBoughtWish = (values: any, groupId: string) => {
  return (dispatch: any) => {
    httpService
      .patch(EEndpoint.GROUP_WISH_PURCHASE, values, {
        params: { groupId: groupId },
      })
      .then(() => {
        history.goBack();
      });
  };
};

export const closeGroupMoneybox = (values: any, groupId: string) => {
  return () => {
    httpService
      .patch(EEndpoint.GROUP_USE_MONEYBOX, values, {
        params: { groupId: groupId },
      })
      .then(() => {
        history.goBack();
      });
  };
};

export const sendGroupEmail = (emailType: TGroupEmail, groupId: string) => {
  return async (dispatch: any) => {
    await httpService.post(
      EEndpoint.GROUP_SENDMAIL,
      { emailType },
      { params: { groupId } },
    );
    const notification: INotification = {
      type: ENotification.SUCCESS,
      msg1: 'success.group-email-sent',
    };
    dispatch(returnNotification(notification));
  };
};

export const editGroup = (body: any, images: any, groupId: any) => {
  return async (dispatch: any) => {
    const res: any = await httpService.put(EEndpoint.GROUP_ID, body, {
      params: { groupId: groupId },
    });
    const imgObjKeys = Object.keys(images);
    for await (const key of imgObjKeys) {
      if (!!res && res.id) {
        await httpService.post(EEndpoint.GROUP_MAIN_PHOTO_UPLOAD, images[key], {
          params: {
            groupId: groupId,
          },
        });
      }
    }
    history.push(ERoutes.GROUP_LIST);
  };
};

export const deleteGroup = (groupId: any) => {
  return (dispatch: any) => {
    httpService
      .delete(EEndpoint.GROUP, { params: { groupId: groupId } })
      .then(() => {
        dispatch(loadGroupList());
      });
  };
};
